/**
 * 빌원 인보이스 목록.
 *
 *   GET /hb2/{csp}/billing/v1/invoices?email=...    (csp = gcp | azure | …)
 *
 * 인증 —
 *  - **로컬**: 개발 서버 프록시(`vue.config.js`)가 `/hb2` 를 빌원으로 넘기면서
 *    `.env` 의 인증(SSO 토큰 또는 API 키)을 붙인다. 프록시는 서버라 CORS 를 안 탄다.
 *  - **운영**: 앞단(게이트웨이/CloudFront)이 오리진으로 갈 때 API 키를 붙인다 (BO-2921).
 *
 * 어느 쪽이든 **브라우저는 자격증명을 들고 있지 않다.**
 *
 * 빌원 응답은 hb2 와 모양이 다르다:
 *  - 응답을 { data, totalCount } 로 감싼다        빌원 목록 API 공통 규약
 *  - 필드 이름이 camelCase                        invoiceMonth, finalCost …
 *  - 청구월에 하이픈이 있다                        "2026-04"  (hb2 는 "202604")
 *  - 인보이스 번호가 문자열                        INV-2026-54807
 *  - 통화가 하나                                  hb2 처럼 KRW/USD 두 벌이 아니다
 *  - 식별자가 둘                                  invoiceNo(표시) / invoiceId(PDF 주소)
 *
 * 아래 toHb2Row 가 그것을 화면이 쓰는 hb2 모양으로 옮긴다.
 */

import config from '@/config'

// 경로의 CSP 세그먼트는 **실행 시점에** 플랫폼 설정에서 읽는다 (BO-3678).
//
// 이 프론트 한 벌이 CSP 6개를 돌리므로 `gcp` 를 박아 두면 Azure 에서 GCP 인보이스를 부른다.
// 상수로 두지 않고 함수인 이유는 `window.servicePlatform` 이 번들 로드 뒤에 정해지기 때문이고,
// 인자로 넘기지 않는 이유는 호출부(4곳)마다 빠뜨릴 수 있어서다.
const invoicesBase = () => `/hb2/${config.platform.id}/billing/v1/invoices`

// 고객에게 보여줄 첫 청구월. 값은 환경별 설정에서 온다 (src/config/env).
// 이 이전 건은 hb2 가 이미 발행했으므로 빌원 것을 겹쳐 보여주면
// 같은 달 인보이스가 두 번 나온다.
//
// 빌원 목록 API 는 fromMonth 가 없으면 월 조건을 아예 안 건다 — 기본값도 없다.
// 그래서 값이 있으면 반드시 붙여야 한다. 형식은 YYYY-MM (202608 은 에러).
// 값이 없는 환경(스테이징)은 안 붙여서 전 기간을 본다.
const firstMonthParam = () => {
  const month = (config.invoice.billone || {}).firstMonth
  return month ? `&fromMonth=${month}` : ''
}

// 개발 서버 프록시(vue.config.js)가 /hb2 를 빌원으로 넘기면서 인증 헤더를 붙인다.
// 브라우저는 토큰을 들고 있지 않다.
const fetchList = (email) =>
  window
    .fetch(
      `${invoicesBase()}?email=${encodeURIComponent(
        email
      )}${firstMonthParam()}`,
      {
        credentials: 'omit',
      }
    )
    .then((res) => {
      if (!res.ok) {
        throw new Error(`빌원 인보이스 조회 실패 (HTTP ${res.status})`)
      }
      return res.json()
    })

// PDF·이용내역은 같은 게이트웨이의 다른 경로로 받는다.
// 브라우저가 이 주소를 그대로 연다 (CloudFront 가 오리진으로 갈 때 인증을 붙인다).
//
// email 을 붙이는 이유 (BO-3684) — 빌원이 **단건 경로에서도 수신인인지 확인**하도록 바뀌었다.
// 그 전에는 invoiceId 만 알면 남의 인보이스가 열렸다. 그 식별자는 시각 기반이라 난수가 아니라서
// 「모르면 못 연다」가 성립하지 않았다.
//
// 안 붙이면 **400** 이다. 목록과 같은 이메일을 그대로 넘긴다.
const emailParam = (email) => `?email=${encodeURIComponent(email)}`
const pdfUrl = (invoiceId, email) =>
  `${invoicesBase()}/${invoiceId}/pdf${emailParam(email)}`
const usageUrl = (invoiceId, email) =>
  `${invoicesBase()}/${invoiceId}/usage${emailParam(email)}`

// 빌원 응답 한 건 → 화면(Invoice.vue)이 읽는 hb2 모양
//
// email 을 함께 받는 이유: 아래 다운로드 링크가 단건 경로라 수신인 확인이 필요하다 (BO-3684).
// 목록을 조회할 때 쓴 이메일을 그대로 흘려보낸다 — 화면이 따로 들고 있을 필요가 없다.
const toHb2Row = (email) => (row) => ({
  // 「번호」 열은 Invoice.vue 가 hb2 번호 뒤로 이어 붙인다. 여기서는 빌원 번호만 넘긴다.
  billone_invoice_no: row.invoiceNo, // 화면엔 안 보인다. 번호를 매길 때 정렬 기준으로 쓴다
  billone_invoice_id: row.invoiceId, // 화면엔 안 보인다. 상세·다운로드 주소에 쓴다
  name: row.name,
  invoice_month: String(row.invoiceMonth).replace('-', ''), // 2026-04 → 202604
  original_costs: row.originalCosts,
  discount: row.discount,
  vat: row.vat,
  final_cost: row.finalCost,
  charge_currency: row.chargeCurrency,
  created_at: row.createdAt,

  // 「환율」 열은 0 으로 채운다 (BO-4322).
  //
  // 빌원 응답에는 환율이 없다. 그런데 화면은 `item.exchange_rate | round2` 로 읽으므로
  // 이 자리를 비우면 `round2(undefined)` 가 **NaN** 을 찍는다 (Math.round(NaN)/100).
  //
  // hb2 가 발행하던 건도 이 열은 전부 0 이었다 — 대고객 청구가 원화라 환산이 없다.
  // 0 을 넣는 것이 「값이 없다」를 그대로 옮기는 셈이고 기존 화면과도 같다.
  // 환율로 청구하는 CSP 가 생기면 그때 빌원이 값을 내려주고 여기서 받으면 된다.
  exchange_rate: 0,

  // 다운로드. 링크가 있으면 화면이 hb2 경로 대신 이걸 연다.
  invoice_pdf_url: pdfUrl(row.invoiceId, email),
  usage_csv_url: usageUrl(row.invoiceId, email),

  // 리포트는 이번 범위 밖 — 빌원 건은 리포트 버튼을 안 그린다.
  report_pdf_url: null,
})

// ── 이용내역 미리보기 ────────────────────────────────────────
//
// 빌원은 평평한 행 목록을 준다. hb2 화면(InvoiceDetail.vue)은
// 프로젝트별로 묶인 usage_data 를 기대하므로 여기서 묶어 준다.
// 묶고 나면 화면의 putUsageData 가 합계·정렬·Total 행을 알아서 만든다.
const fetchUsageDetail = (invoiceId, email) =>
  window
    .fetch(`${invoicesBase()}/${invoiceId}/usage-detail${emailParam(email)}`, {
      credentials: 'omit',
    })
    .then((res) => {
      if (!res.ok) {
        throw new Error(`빌원 이용내역 조회 실패 (HTTP ${res.status})`)
      }
      return res.json()
    })

// 평평한 행들 → hb2 usage_data (프로젝트별 묶음)
// 빌원은 CSP 마다 **그 CSP 의 용어**로 필드 이름을 준다 (BO-3679 결정).
// GCP 는 project, Azure 는 subscription 이다. 구독을 project 라 부르면 API 스펙이
// 사실과 달라지므로 이름을 맞추는 쪽이 아니라 **여기서 흡수**한다.
//
// CSP별 매핑표를 두지 않는 이유는 이름이 서로 겹치지 않기 때문이다 — 표를 두면 CSP 가
// 늘 때마다 표도 늘어난다. 먼저 정의된 후보를 쓴다.
const pick = (row, ...names) => {
  const found = names.find((n) => row[n] !== undefined && row[n] !== null)
  return found === undefined ? null : row[found]
}

const groupByProject = (rows) => {
  const groups = new Map()

  rows.forEach((row) => {
    // 구독/프로젝트가 없으면 빈 문자열 하나로 모은다 (hb2 가 그렇게 저장한다).
    const key = pick(row, 'subscriptionId', 'projectId') || ''
    if (!groups.has(key)) {
      const accountId = pick(row, 'linkedAccountId', 'billingAccountId')
      groups.set(key, {
        account_id: accountId,
        account_name: row.accountName || accountId,
        project_id: key,
        project_name: pick(row, 'subscriptionName', 'projectName'),
        currency: row.currency,
        data: [],
      })
    }
    groups.get(key).data.push({
      service_id: row.serviceId,
      service_name: row.serviceName,
      sku_id: row.skuId,
      sku_name: row.skuName,
      usage_start_date: row.usageStartDate,
      usage_end_date: row.usageEndDate,
      cost_type: row.costType,
      cost: row.cost,
    })
  })

  return [...groups.values()]
}

// 계약 할인 등을 hb2 가 쓰는 모양으로 옮긴다 (BO-3014).
//
// hb2 는 할인을 usage_data 안에 한 항목으로 넣는다 — 그래서 개요 표 마지막 줄에
// 「Invoice Discount (4%)  -196,501」 처럼 찍힌다. 화면(InvoiceDetail.vue)에는
// 할인을 따로 그리는 코드가 없다. 같은 자리에 같은 모양으로 넣어야 한다.
//
// 금액은 음수로 넣는다 — 화면이 data 의 cost 를 더해 total_cost 를 다시 만든다.
//
// 표시 문구는 **임시로 여기서 정한다.**
// 빌원은 지금 '계약 할인' 이라는 고정 문구를 준다(Hb2InvoiceUsageDetailProvider 의
// CONTRACT_DISCOUNT_LABEL). 기존 하이퍼빌링은 같은 자리에 'Invoice Discount (4%)'
// 처럼 할인율까지 붙여 보여주므로, 고객이 보던 것과 덜 어긋나게 당분간 이 문구를 쓴다.
//
// 빌원에 원래 이름과 할인율을 물어봐 두었다. 답이 오면 이 상수를 지우고
// d.label 을 그대로 쓴다 — 할인 명칭은 정산 쪽 용어라 빌원이 정하는 게 맞다.
const DISCOUNT_LABEL = 'Invoice Discount'

const toDiscountGroup = (d) => ({
  account_id: null,
  account_name: null,
  project_id: DISCOUNT_LABEL, // 개요 표의 「프로젝트 아이디」 칸에 이 문구가 찍힌다
  project_name: '',
  currency: d.currency,
  data: [
    {
      service_id: '',
      service_name: DISCOUNT_LABEL,
      sku_id: '',
      sku_name: '',
      cost: -d.amount,
    },
  ],
})

// 빌원 파일을 받아서 hb2 방식 이름으로 저장한다.
//
// hb2 는 이용내역서를 「번호.csv」 로 준다 (gcp-billing-back 의 download.js).
// 빌원은 자기 id 를 써서 「INV-0000000000JBP.csv」 로 주므로, 그대로 두면
// 고객이 받는 파일 이름이 예전과 달라진다. 그래서 한 번 받아서 이름만 바꿔 저장한다.
//
// 주소가 화면과 같은 도메인이어야 이름 바꾸기가 먹는다. 로컬에서는 개발 서버
// 프록시가, 운영에서는 CloudFront 가 같은 도메인으로 묶어 준다.
const download = (url, filename) =>
  window
    .fetch(url, { credentials: 'omit' })
    .then((res) => {
      if (!res.ok) {
        throw new Error(`빌원 파일 내려받기 실패 (HTTP ${res.status})`)
      }
      return res.blob()
    })
    .then((blob) => {
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(objectUrl)
    })

export default {
  download,

  // 조회 키는 사용자 번호가 아니라 이메일이다 — 빌원은 hb2 의 userSeq 를 모른다.
  invoice: (email) =>
    fetchList(email).then((res) => (res.data || []).map(toHb2Row(email))),

  // 화면은 usage_data 를 담은 객체 하나를 기대한다.
  // email 은 단건 경로의 수신인 확인용이다 (BO-3684) — 빠뜨리면 400.
  invoiceDetail: (invoiceId, email) =>
    fetchUsageDetail(invoiceId, email).then((res) => {
      const head = res.invoice || {}
      return {
        seq: head.invoiceNo, // 화면 맨 위 「인보이스 번호」
        name: head.name,
        company: head.company,
        invoice_month: String(head.invoiceMonth || '').replace('-', ''),
        cost: head.cost,
        currency: head.currency,
        usage_data: [
          ...groupByProject(res.data || []),
          ...(res.discounts || []).map(toDiscountGroup),
        ],
      }
    }),
}
